/* eslint-disable no-shadow */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  Button,
  getTranslation,
  ProfileMenu,
} from '@resy/ui-kitchen';

import inFrame from '../../utils/inFrame';
import OPENAI_WIDGET from '../../constants/srcTypes';
import PAYMENT from '../../constants/pageTypes';

import Arrow from '../../svg/dynamic/ic_chevron-left_20x20.svg';
import CloseThick from '../../svg/dynamic/ic_close_20x20.svg';

import './WidgetNav.scss';
import { userLogoutThunk } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { getLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import { setSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';

class WidgetNav extends React.Component {
  getTransitionVal = (opacity) => {
    const diff = (255 - 145) * (1 - opacity);
    return 145 + (diff >= 0 ? diff : 0);
  }

  onClose = () => {
    const {
      Analytics: {
        event,
      },
      pageContext: context,
      venueName: venue,
      type,
      title,
      eventName,
    } = this.props;

    if (context && eventName) {
      event(
        eventName,
        {
          context,
          venue,
          type,
          title,
        }
      );
    }

    window.parent.postMessage({ event: 'close' }, '*');
  }

  onLogin = () => {
    const {
      navigate,
    } = this.props;
    const currentPath = getLocationPath();
    navigate('/login');
    if (currentPath !== '/reset-password') {
      setSearchParams('next', currentPath);
      setSearchParams('prev', currentPath);
    }
  }

  onLogout = async () => {
    const {
      logoutRedirect, navigate, navVenueId, userLogoutThunk,
    } = this.props;

    // side effect
    // eslint-disable-next-line no-unused-expressions
    window.opener?.postMessage({ event: 'logoutSync' }, '*') || window.parent?.postMessage({ event: 'logoutSync' }, '*');
    await userLogoutThunk();
    if (logoutRedirect) {
      navigate(logoutRedirect);
    } else if (navVenueId) {
      navigate(`/venues/${navVenueId}`);
    }
  }

  onBackClick = () => {
    const {
      backPath,
      navigate,
      search,
    } = this.props;

    navigate(backPath);
    if (search) {
      Object.entries(search).forEach(([key, val]) => setSearchParams(key, val));
    }
  }

  renderBack = () => {
    const {
      invert,
      backPath,
      showBack,
      titleOpacity,
      sourceId,
      type,
    } = this.props;

    if (!backPath || !showBack || (sourceId === OPENAI_WIDGET && type !== PAYMENT)) {
      return null;
    }

    let style;

    if (invert) {
      const val = this.getTransitionVal(titleOpacity);
      style = {
        fill: `rgba(${val}, ${val}, ${val}, 1)`,
      };
    }
    return (
      <Button
        aria-label={getTranslation({ translationKey: 'go_back', fallback: 'Go back' })}
        className="WidgetNav__back"
        data-test-id="widget_nav-button-back"
        onClick={this.onBackClick}
        variant="back"
      >
        <svg>
          <Arrow style={style} />
        </svg>
      </Button>
    );
  }

  renderClose = () => {
    const {
      invert,
      titleOpacity,
      sourceId,
    } = this.props;

    if (!inFrame() || sourceId === OPENAI_WIDGET) {
      return null;
    }

    let style;

    if (invert) {
      const val = this.getTransitionVal(titleOpacity);
      style = {
        fill: `rgba(${val}, ${val}, ${val}, 1)`,
      };
    }

    return (
      <Button
        aria-label={getTranslation({ translationKey: 'close_booking', fallback: 'close booking' })}
        className="WidgetNav__close"
        onClick={this.onClose}
        variant="close"
      >
        <CloseThick style={style} />
      </Button>
    );
  }

  renderProfileMenu = () => {
    const {
      Analytics,
      domain,
      isConcierge,
      items,
      showProfileMenu,
      sourceId,
    } = this.props;

    return (
      <ProfileMenu
        Analytics={Analytics}
        domain={domain}
        isConcierge={isConcierge}
        items={items}
        onLogin={this.onLogin}
        onLogout={this.onLogout}
        showProfileMenu={showProfileMenu}
        sourceId={sourceId}
      >
        {this.renderClose()}
      </ProfileMenu>
    );
  }

  render() {
    const {
      invert,
      pageContext,
      sourceId,
    } = this.props;

    if (sourceId === OPENAI_WIDGET) {
      return null;
    }

    return (
      <div
        className={classnames('WidgetNav', {
          'WidgetNav--invert': invert,
          'WidgetNav--white-bg': pageContext === 'reservation details',
        })}
      >
        {this.renderBack()}
        {this.renderProfileMenu()}
      </div>
    );
  }
}

const widgetNavPropTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  backPath: PropTypes.string,
  domain: PropTypes.string,
  invert: PropTypes.bool,
  isConcierge: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      path: PropTypes.string,
      translationKey: PropTypes.string,
      activeMatcher: PropTypes.string,
      isConcierge: PropTypes.bool,
    })
  ),
  logoutRedirect: PropTypes.string.isRequired,
  navigate: PropTypes.func.isRequired,
  navVenueId: PropTypes.number.isRequired,
  sourceId: PropTypes.string,
  pageContext: PropTypes.string,
  search: PropTypes.shape({
    date: PropTypes.string,
    seats: PropTypes.number,
  }),
  showBack: PropTypes.bool,
  showProfileMenu: PropTypes.bool,
  title: PropTypes.string,
  titleOpacity: PropTypes.number,
  userLogoutThunk: PropTypes.func.isRequired,
  venueName: PropTypes.string,
  type: PropTypes.string,
  eventName: PropTypes.string,
};

WidgetNav.defaultProps = {
  backPath: null,
  domain: null,
  invert: false,
  isConcierge: false,
  items: undefined,
  pageContext: undefined,
  search: null,
  showBack: false,
  showProfileMenu: false,
  sourceId: undefined,
  title: undefined,
  titleOpacity: undefined,
  venueName: undefined,
  type: undefined,
  eventName: 'Widget Closed',
};
WidgetNav.propTypes = widgetNavPropTypes;

const mapStateToProps = ({ resyRouter }) => {
  const { logoutRedirect, navVenueId, sourceId } = resyRouter;
  return {
    logoutRedirect,
    navVenueId,
    sourceId,
  };
};

const mapDispatchToProps = {
  userLogoutThunk,
};

export { widgetNavPropTypes, WidgetNav as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(WidgetNav);
